/**
 * Pure state/derivation logic for the exercise picker
 * (`SportActivitySelectorSearch` + `SportActivitySelectorModal`).
 *
 * Kept free of React Native / PowerSync / Apollo runtime deps (type-only,
 * structural interfaces) so it runs under the fast Node `unit` jest project and
 * both the search input and the modal list can share one tested source of truth
 * for: relevance ordering, muscle-group filtering applied to the *data* (never
 * inside a row renderer), and the loading/error/no-results/results view state.
 */

/** Anything with a stable `id` — a hydrated sport-activity record. */
export interface PickerIdentifiable {
  id: string;
}

/**
 * The muscle-target fields the picker filters/groups on. Fields are nullable to
 * match the generated PowerSync record type; null-keyed targets are dropped
 * during grouping and never match a specific group filter.
 */
export interface MuscleTargetLike {
  sportActivityId: string | null;
  muscleGroup: string | null;
}

/** The four mutually-exclusive states the results area can render. */
export type PickerViewState = 'loading' | 'error' | 'no-results' | 'results';

export class ExercisePicker {
  /**
   * Reorder hydrated records into `ids` order (the server's relevance order),
   * dropping any id that did not hydrate locally. Input arrays are untouched.
   */
  static orderByIds<T extends PickerIdentifiable>(
    ids: readonly string[],
    records: readonly T[],
  ): T[] {
    const byId = new Map(records.map((r) => [r.id, r]));
    const ordered: T[] = [];
    for (const id of ids) {
      const record = byId.get(id);
      if (record) {
        ordered.push(record);
      }
    }
    return ordered;
  }

  /**
   * Group a flat muscle-target list (one batched query for the whole result
   * set) into a lookup keyed by `sportActivityId`, preserving encounter order.
   */
  static groupMuscleTargets<T extends MuscleTargetLike>(
    targets: readonly T[],
  ): Map<string, T[]> {
    const byActivity = new Map<string, T[]>();
    for (const target of targets) {
      const key = target.sportActivityId;
      if (key == null) {
        continue;
      }
      const existing = byActivity.get(key);
      if (existing) {
        existing.push(target);
      } else {
        byActivity.set(key, [target]);
      }
    }
    return byActivity;
  }

  /**
   * Filter records to those whose muscle targets include `filter`. The `'all'`
   * sentinel returns every record. Records with no loaded targets are excluded
   * once a specific group is active. Applied to the data array so filtered-out
   * exercises are never mounted.
   */
  static filterByMuscleGroup<T extends PickerIdentifiable>(
    records: readonly T[],
    muscleTargetsByActivityId: ReadonlyMap<string, readonly MuscleTargetLike[]>,
    filter: string,
  ): T[] {
    if (filter === 'all') {
      return [...records];
    }
    return records.filter((record) => {
      const targets = muscleTargetsByActivityId.get(record.id);
      return !!targets && targets.some((t) => t.muscleGroup === filter);
    });
  }

  /**
   * Resolve the results-area view state. Error takes precedence (a persistent
   * failure shouldn't be masked by an in-flight refetch); `no-results` only
   * once settled with zero results, so a blank list never stands in for
   * "still loading".
   */
  static resolveViewState(input: {
    isLoading: boolean;
    hasError: boolean;
    resultCount: number;
  }): PickerViewState {
    if (input.hasError) {
      return 'error';
    }
    if (input.isLoading) {
      return 'loading';
    }
    if (input.resultCount === 0) {
      return 'no-results';
    }
    return 'results';
  }
}
